/**
 * RecipeCenter — Community Recipe Book. Shared library of AI-generated
 * freeze-dry recipes saved from the Home Meal Prep Calculator (and, in
 * the future, from the Label Maker's Recipe Builder).
 *
 * Powered by:
 *   GET  /api/tools/recipe-center?q=&tags=&mine=
 *   GET  /api/tools/recipe-center/tags        (chip counts)
 *   GET  /api/tools/recipe-center/{id}
 *   DELETE /api/tools/recipe-center/{id}
 *   POST /api/tools/recipe-center/{id}/generate-image  (Gemini Nano Banana)
 */

import React, { useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import {
  BookOpen, Search, Sparkles, Trash2, ImageIcon, Loader2, ChefHat,
  ExternalLink, Clock, User, ThumbsUp, ThumbsDown, Plus, X, ArrowLeft,
  Home, Calculator, UtensilsCrossed, Tag, FileText, Printer, CheckSquare, Square,
} from 'lucide-react';
import ToolDisclaimer from '../components/tools/ToolDisclaimer';
import RecipeCardModal from '../components/tools/RecipeCardModal';
import ToolNavBar from '../components/tools/ToolNavBar';
import { buildBrandedHtml, addCanvasAsPages } from '../utils/recipeCardTemplate';

const TAG_LABEL = {
  vegan: 'Vegan',
  vegetarian: 'Vegetarian',
  gluten_free: 'Gluten-Free',
  dairy_free: 'Dairy-Free',
  low_sodium: 'Low Sodium',
  low_sugar: 'Low Sugar',
  diabetic_friendly: 'Diabetic-Friendly',
  keto: 'Keto',
  paleo: 'Paleo',
  pescatarian: 'Pescatarian',
  nut_free: 'Nut-Free',
  high_protein: 'High Protein',
};

export default function RecipeCenter() {
  const { API, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [recipes, setRecipes]   = useState([]);
  const [tagCounts, setTagCounts] = useState({});
  const [loading, setLoading]   = useState(false);
  const [q, setQ]               = useState('');
  const [activeTags, setActiveTags] = useState([]);
  const [mineOnly, setMineOnly] = useState(false);
  const [viewMeal, setViewMeal] = useState(null);   // triggers RecipeCardModal
  const [imgLoading, setImgLoading] = useState({}); // {recipeId: true}
  const [showAdd, setShowAdd] = useState(false);
  const [custom, setCustom] = useState({
    name: '', servings: 4, ingredients: '', instructions: '', tags: [],
  });
  const [savingCustom, setSavingCustom] = useState(false);
  const [selected, setSelected] = useState({});  // {recipeId: true}
  const [bulkBusy, setBulkBusy] = useState(false);

  const toggleSelect = (recipeId) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[recipeId]) delete next[recipeId]; else next[recipeId] = true;
      return next;
    });
  };
  const clearSelection = () => setSelected({});
  const selectAllVisible = () => {
    const next = {};
    recipes.forEach((r) => { next[r.id] = true; });
    setSelected(next);
  };
  const selectedRecipes = recipes.filter((r) => selected[r.id]);
  const selectedCount = selectedRecipes.length;

  // Wait until every <img.photo> in the given document is either loaded
  // or errored (or 4s has passed) so html2canvas doesn't snap blank tiles.
  const waitForImages = async (doc) => {
    const imgs = Array.from(doc.querySelectorAll('img.photo'));
    await Promise.all(imgs.map((img) => (
      img.complete
        ? Promise.resolve()
        : new Promise((res) => {
            img.addEventListener('load',  res, { once: true });
            img.addEventListener('error', res, { once: true });
            setTimeout(res, 4000);
          })
    )));
  };

  // Render a booklet's HTML in an off-screen iframe one "page" at a time
  // (splitting on `.recipe-page` and the cover) so each becomes its own
  // 8.5×11" PDF page with clean breaks.
  const bulkExportPdf = async () => {
    if (selectedCount === 0) { toast.error('Select at least one recipe.'); return; }
    setBulkBusy(true);
    const iframe = document.createElement('iframe');
    Object.assign(iframe.style, {
      position: 'fixed', left: '-9999px', top: '0',
      width: '816px', height: '1056px', border: '0', background: '#fff',
    });
    document.body.appendChild(iframe);
    try {
      const pages = selectedRecipes.map((r) => ({
        view: r,
        imageUrl: r.image_data_url || null,
      }));
      const html = buildBrandedHtml(pages, {
        withPrintScript: false,
        cover: {
          coverTitle: 'Freeze Dry Academy Recipe Booklet',
          coverSubtitle: `Community Recipe Book — ${selectedCount} recipe${selectedCount === 1 ? '' : 's'}`,
        },
      });
      const doc = iframe.contentDocument;
      doc.open(); doc.write(html); doc.close();
      await new Promise((r) => setTimeout(r, 200));
      await waitForImages(doc);

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
      const pageEls = Array.from(doc.querySelectorAll('.cover-page, .recipe-page'));
      for (let i = 0; i < pageEls.length; i += 1) {
        const el = pageEls[i];
        // eslint-disable-next-line no-await-in-loop
        const canvas = await html2canvas(el, {
          background: '#ffffff', scale: 2, useCORS: true,
          width: 816, windowWidth: 816,
        });
        // Each recipe/cover starts a fresh page (except the first). If a
        // recipe is taller than one letter page, addCanvasAsPages spills
        // the overflow onto extra pages so nothing gets clipped.
        addCanvasAsPages(pdf, canvas, { startNewPage: i > 0 });
      }
      pdf.save(`recipe-booklet-${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success(`Downloaded ${selectedCount}-recipe booklet.`);
    } catch (e) {
      toast.error(`Booklet export failed: ${e.message || e}`);
    } finally {
      iframe.remove();
      setBulkBusy(false);
    }
  };

  const bulkPrintBooklet = () => {
    if (selectedCount === 0) { toast.error('Select at least one recipe.'); return; }
    const pages = selectedRecipes.map((r) => ({
      view: r,
      imageUrl: r.image_data_url || null,
    }));
    const html = buildBrandedHtml(pages, {
      withPrintScript: true,
      cover: {
        coverTitle: 'Freeze Dry Academy Recipe Booklet',
        coverSubtitle: `Community Recipe Book — ${selectedCount} recipe${selectedCount === 1 ? '' : 's'}`,
      },
    });
    const win = window.open('', '_blank', 'width=900,height=1100');
    if (!win) { toast.error('Popup blocked — allow popups to print the booklet.'); return; }
    win.document.write(html);
    win.document.close();
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set('q', q.trim());
      if (mineOnly) params.set('mine', 'true');
      for (const t of activeTags) params.append('tags', t);
      const [{ data: list }, { data: tags }] = await Promise.all([
        axios.get(`${API}/tools/recipe-center?${params.toString()}`),
        axios.get(`${API}/tools/recipe-center/tags`),
      ]);
      setRecipes(list.recipes || []);
      setTagCounts(tags.tags || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load recipes');
    } finally { setLoading(false); }
  }, [API, q, activeTags, mineOnly]);

  useEffect(() => { load(); }, [load]);

  // Auto-hydrate pending stubs the first time the operator lands on the
  // Recipe Book. We fire recipe-card-ai in the background at most 2 at
  // a time so we don't stampede the LLM. Each successful call flips
  // `pending_details` on the row via the recipe-card-ai upgrade path.
  useEffect(() => {
    const pending = recipes.filter((r) => r.pending_details);
    if (pending.length === 0) return;
    let cancelled = false;
    let inFlight = 0;
    const queue = pending.slice();   // shallow copy
    const nextOne = async () => {
      if (cancelled) return;
      const target = queue.shift();
      if (!target) return;
      inFlight += 1;
      try {
        const { data } = await axios.post(`${API}/tools/recipe-card-ai`, {
          meal_name: target.name,
          servings:  target.servings || 4,
        });
        if (cancelled) return;
        setRecipes((prev) => prev.map((r) => (r.id === target.id
          ? { ...r, ...data, id: target.id, pending_details: false }
          : r)));
        // Follow-up: generate the AI photo so the card has visual context
        // in the grid AND on the printable recipe. Runs sequentially with
        // the hydration to keep pressure low on the image endpoint.
        if (!target.image_data_url) {
          try {
            const { data: img } = await axios.post(
              `${API}/tools/recipe-center/${target.id}/generate-image`,
            );
            if (cancelled) return;
            setRecipes((prev) => prev.map((r) => (r.id === target.id
              ? { ...r, image_data_url: img.image_data_url }
              : r)));
          } catch { /* silent — user can retry from the grid */ }
        }
      } catch { /* silent — user can click the card to retry */ }
      finally {
        inFlight -= 1;
        if (!cancelled) nextOne();
      }
    };
    // Start up to 2 concurrent workers.
    nextOne(); nextOne();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [recipes.some((r) => r.pending_details)]);

  const toggleTag = (t) => {
    setActiveTags((prev) => prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]);
  };

  const generateImage = async (recipe) => {
    setImgLoading((s) => ({ ...s, [recipe.id]: true }));
    try {
      const { data } = await axios.post(`${API}/tools/recipe-center/${recipe.id}/generate-image`);
      setRecipes((prev) => prev.map((r) => (r.id === recipe.id
        ? { ...r, image_data_url: data.image_data_url }
        : r)));
      toast.success(data.cached ? 'Image loaded.' : 'AI photo generated.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Image generation failed');
    } finally {
      setImgLoading((s) => { const c = { ...s }; delete c[recipe.id]; return c; });
    }
  };

  const deleteRecipe = async (recipe) => {
    if (!window.confirm(`Delete "${recipe.name}"? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/tools/recipe-center/${recipe.id}`);
      setRecipes((prev) => prev.filter((r) => r.id !== recipe.id));
      toast.success('Recipe deleted.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const vote = async (recipe, want) => {
    // Toggle: clicking the same vote clears it.
    const next = recipe.my_vote === want ? 0 : want;
    try {
      const { data } = await axios.post(
        `${API}/tools/recipe-center/${recipe.id}/vote`,
        { vote: next },
      );
      setRecipes((prev) => prev.map((r) => (r.id === recipe.id
        ? { ...r, upvotes: data.upvotes, downvotes: data.downvotes, my_vote: data.my_vote }
        : r)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Vote failed');
    }
  };

  const saveCustom = async () => {
    if (!custom.name.trim()) { toast.error('Give the recipe a name.'); return; }
    setSavingCustom(true);
    try {
      const payload = {
        name: custom.name.trim(),
        servings: Number(custom.servings) || 4,
        ingredients: custom.ingredients.split('\n').map((line) => {
          const l = line.trim();
          if (!l) return null;
          // Parse "500 g pasta" | "2 cups broth" | free-form
          const m = l.match(/^([\d.]+)\s*(g|kg|ml|l|cup|cups|tsp|tbsp|whole)?\s+(.+)$/i);
          return m
            ? { quantity: Number(m[1]) || 0, unit: m[2] || '', item: m[3] }
            : { quantity: 0, unit: '', item: l };
        }).filter(Boolean),
        instructions: custom.instructions.split('\n').map((s) => s.trim()).filter(Boolean),
        dietary_tags: custom.tags,
      };
      const { data: saved } = await axios.post(`${API}/tools/recipe-center`, payload);
      setRecipes((prev) => [saved, ...prev]);
      setShowAdd(false);
      setCustom({ name: '', servings: 4, ingredients: '', instructions: '', tags: [] });
      toast.success('Recipe added — generating photo…');
      axios.post(`${API}/tools/recipe-center/${saved.id}/generate-image`)
        .then((r) => setRecipes((prev) => prev.map((x) => (x.id === saved.id ? { ...x, image_data_url: r.data.image_data_url } : x))))
        .catch(() => { /* silent */ });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSavingCustom(false); }
  };

  return (
    <div className="container mx-auto px-6 max-w-6xl py-6 space-y-6" data-testid="recipe-center-root">
      <ToolNavBar activeSlug="recipe-center" />
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <BookOpen className="w-7 h-7 text-primary" />
          <div>
            <h1 className="text-3xl font-extrabold">Community Recipe Book</h1>
            <p className="text-sm text-muted-foreground">
              Every AI-generated freeze-dry recipe lives here — vote, filter,
              print. Add your own from-scratch recipes too.
            </p>
          </div>
        </div>
        {user && (
          <Button type="button" onClick={() => setShowAdd(true)} data-testid="rc-add-btn">
            <Plus className="w-4 h-4 mr-1" /> Add custom recipe
          </Button>
        )}
      </div>

      <ToolDisclaimer
        testid="rc-disclaimer"
        intro={
          <p>
            Every recipe here was AI-drafted and saved by a community member from
            the <span className="font-semibold">Home Meal Prep Calculator</span>.
            Nutrition, cost, and freeze-dry parameters are estimates — always
            verify against a certified source before serving to customers.
          </p>
        }
      />

      {/* Search + filter row */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex gap-2 flex-wrap items-center">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search recipes by name…"
                className="pl-9"
                data-testid="rc-search"
              />
            </div>
            {user && (
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={mineOnly}
                  onChange={(e) => setMineOnly(e.target.checked)}
                  data-testid="rc-mine-only"
                />
                My saves only
              </label>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="rc-tag-chips">
            {Object.keys(TAG_LABEL).map((t) => {
              const on = activeTags.includes(t);
              const count = tagCounts[t] || 0;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => toggleTag(t)}
                  disabled={count === 0}
                  className={
                    'text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full border transition-colors ' +
                    (on
                      ? 'bg-primary text-primary-foreground border-primary'
                      : count === 0
                      ? 'bg-background text-muted-foreground/50 border-border opacity-50 cursor-not-allowed'
                      : 'bg-background text-foreground border-border hover:border-primary/60')
                  }
                  data-testid={`rc-tag-${t}`}
                >
                  {TAG_LABEL[t]} {count > 0 && <span className="opacity-70 ml-1">({count})</span>}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : recipes.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            <ChefHat className="w-12 h-12 mx-auto mb-3 opacity-40" />
            No recipes match those filters yet.{' '}
            <a href="/food-prep-resource" className="text-primary hover:underline">
              Head to the Home Meal Prep Calculator
            </a>{' '}
            to generate one and click "Save" from any recipe card.
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Sticky bulk-selection action bar — only rendered when the
              operator has selected at least one recipe. Two exits:
              download as a branded booklet PDF or fire off Print for a
              physical booklet using the SAME shared HTML template. */}
          {selectedCount > 0 && (
            <div
              className="sticky top-2 z-30 bg-primary text-primary-foreground rounded-lg shadow-lg px-4 py-2 flex items-center gap-3 flex-wrap"
              data-testid="rc-bulk-bar"
            >
              <CheckSquare className="w-4 h-4" />
              <span className="text-sm font-semibold">
                {selectedCount} selected
              </span>
              <Button
                type="button" size="sm" variant="secondary"
                onClick={selectAllVisible}
                data-testid="rc-bulk-select-all"
              >
                Select all visible ({recipes.length})
              </Button>
              <Button
                type="button" size="sm" variant="secondary"
                onClick={clearSelection}
                data-testid="rc-bulk-clear"
              >
                Clear
              </Button>
              <div className="flex-1" />
              <Button
                type="button" size="sm" variant="secondary"
                onClick={bulkPrintBooklet} disabled={bulkBusy}
                data-testid="rc-bulk-print"
              >
                <Printer className="w-3.5 h-3.5 mr-1" /> Print booklet
              </Button>
              <Button
                type="button" size="sm"
                onClick={bulkExportPdf} disabled={bulkBusy}
                className="bg-white text-primary hover:bg-white/90"
                data-testid="rc-bulk-pdf"
              >
                {bulkBusy
                  ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Building…</>
                  : <><FileText className="w-3.5 h-3.5 mr-1" /> Export {selectedCount} as PDF</>}
              </Button>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="rc-grid">
          {recipes.map((r) => (
            <Card key={r.id} className="overflow-hidden flex flex-col relative" data-testid={`rc-recipe-${r.id}`}>
              {/* Selection checkbox — floats over the top-left of the tile.
                  Uses the button variant so it inherits the primary theme
                  colour when selected. */}
              <button
                type="button"
                onClick={() => toggleSelect(r.id)}
                className={
                  'absolute top-2 left-2 z-10 w-7 h-7 rounded-md border-2 flex items-center justify-center transition-colors ' +
                  (selected[r.id]
                    ? 'bg-primary border-primary text-primary-foreground'
                    : 'bg-white/90 border-slate-400 text-slate-500 hover:border-primary')
                }
                aria-label={selected[r.id] ? 'Deselect recipe' : 'Select recipe'}
                data-testid={`rc-select-${r.id}`}
              >
                {selected[r.id] ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
              </button>
              <div className="aspect-video bg-muted relative flex items-center justify-center overflow-hidden">
                {r.image_data_url ? (
                  <img
                    src={r.image_data_url}
                    alt={r.name}
                    className="w-full h-full object-cover"
                    data-testid={`rc-img-${r.id}`}
                  />
                ) : (
                  <div className="flex flex-col items-center gap-1 text-muted-foreground">
                    <ImageIcon className="w-8 h-8" />
                    <Button
                      type="button" size="sm" variant="outline"
                      onClick={() => generateImage(r)}
                      disabled={!!imgLoading[r.id]}
                      data-testid={`rc-gen-img-${r.id}`}
                    >
                      {imgLoading[r.id]
                        ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Painting…</>
                        : <><Sparkles className="w-3 h-3 mr-1" /> Generate photo</>}
                    </Button>
                  </div>
                )}
              </div>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">{r.name}</CardTitle>
                <div className="flex flex-wrap gap-1 mt-1">
                  {(r.dietary_tags || []).slice(0, 4).map((t) => (
                    <span
                      key={t}
                      className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full border border-emerald-600 text-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 dark:text-emerald-300"
                    >
                      {TAG_LABEL[t] || t}
                    </span>
                  ))}
                </div>
              </CardHeader>
              <CardContent className="pt-0 flex-1 flex flex-col justify-between space-y-2">
                    <div className="text-xs text-muted-foreground space-y-1">
                    <div className="flex gap-2 items-center">
                      <ChefHat className="w-3 h-3" /> {r.servings} servings
                      {(r.prep_time_min || r.cook_time_min) && (
                        <>
                          <span className="opacity-60">·</span>
                          <Clock className="w-3 h-3" /> {(r.prep_time_min || 0) + (r.cook_time_min || 0)} min
                        </>
                      )}
                    </div>
                    {r.pending_details && (
                      <div className="text-[10px] font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400 flex items-center gap-1">
                        <Loader2 className="w-2.5 h-2.5 animate-spin" /> Hydrating recipe…
                      </div>
                    )}
                    {r.source_plan_id && r.source_plan_name && (
                      <button
                        type="button"
                        onClick={() => navigate(`/food-prep-resource?plan=${r.source_plan_id}`)}
                        className="text-[10px] text-primary hover:underline text-left"
                        title="Open the meal plan that created this recipe"
                        data-testid={`rc-plan-chip-${r.id}`}
                      >
                        From plan: {r.source_plan_name}
                      </button>
                    )}
                    {r.created_by_email && (
                      <div className="flex items-center gap-1"><User className="w-3 h-3" /> {r.created_by_email}</div>
                    )}
                  </div>
                <div className="flex gap-1 flex-wrap justify-between items-center">
                  <div className="flex gap-0.5 items-center">
                    <Button
                      type="button" size="sm"
                      variant={r.my_vote === 1 ? 'default' : 'outline'}
                      onClick={() => vote(r, 1)}
                      className="h-7 px-2"
                      data-testid={`rc-upvote-${r.id}`}
                    >
                      <ThumbsUp className="w-3 h-3 mr-1" />
                      <span className="text-[10px]">{r.upvotes || 0}</span>
                    </Button>
                    <Button
                      type="button" size="sm"
                      variant={r.my_vote === -1 ? 'default' : 'outline'}
                      onClick={() => vote(r, -1)}
                      className="h-7 px-2"
                      data-testid={`rc-downvote-${r.id}`}
                    >
                      <ThumbsDown className="w-3 h-3 mr-1" />
                      <span className="text-[10px]">{r.downvotes || 0}</span>
                    </Button>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      type="button" size="sm" variant="outline"
                      onClick={() => setViewMeal({ name: r.name, servings: r.servings })}
                      data-testid={`rc-open-${r.id}`}
                    >
                      <ExternalLink className="w-3 h-3 mr-1" /> View
                    </Button>
                    {r.is_mine && (
                      <Button
                        type="button" size="sm" variant="outline"
                        onClick={() => deleteRecipe(r)}
                        data-testid={`rc-delete-${r.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          </div>
        </>
      )}

      <RecipeCardModal
        meal={viewMeal}
        open={!!viewMeal}
        onClose={() => setViewMeal(null)}
      />

      {showAdd && (
        <div
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowAdd(false)}
          data-testid="rc-add-modal"
        >
          <Card
            className="max-w-lg w-full max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
              <CardTitle className="text-base">Add a custom recipe</CardTitle>
              <button
                type="button" onClick={() => setShowAdd(false)}
                className="text-muted-foreground hover:text-foreground"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-xs">Recipe name</Label>
                <Input
                  value={custom.name}
                  onChange={(e) => setCustom({ ...custom, name: e.target.value })}
                  placeholder="Grandma's chicken and rice"
                  data-testid="rc-custom-name"
                />
              </div>
              <div>
                <Label className="text-xs">Servings</Label>
                <Input
                  type="number" min="1"
                  value={custom.servings}
                  onChange={(e) => setCustom({ ...custom, servings: e.target.value })}
                  data-testid="rc-custom-servings"
                />
              </div>
              <div>
                <Label className="text-xs">
                  Ingredients — one per line ("500 g rice", "2 cups broth")
                </Label>
                <Textarea
                  rows={5}
                  value={custom.ingredients}
                  onChange={(e) => setCustom({ ...custom, ingredients: e.target.value })}
                  placeholder={'500 g rice\n2 cups broth\n1 whole chicken'}
                  data-testid="rc-custom-ingredients"
                />
              </div>
              <div>
                <Label className="text-xs">Instructions — one step per line</Label>
                <Textarea
                  rows={5}
                  value={custom.instructions}
                  onChange={(e) => setCustom({ ...custom, instructions: e.target.value })}
                  placeholder={'Boil the broth\nAdd rice, cover for 20 min'}
                  data-testid="rc-custom-instructions"
                />
              </div>
              <div>
                <Label className="text-xs">Dietary tags</Label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {Object.keys(TAG_LABEL).map((t) => {
                    const on = custom.tags.includes(t);
                    return (
                      <button
                        key={t} type="button"
                        onClick={() => setCustom((c) => ({
                          ...c,
                          tags: on ? c.tags.filter((x) => x !== t) : [...c.tags, t],
                        }))}
                        className={
                          'text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ' +
                          (on
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-background text-foreground border-border hover:border-primary/60')
                        }
                        data-testid={`rc-custom-tag-${t}`}
                      >
                        {TAG_LABEL[t]}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
                <Button
                  onClick={saveCustom}
                  disabled={savingCustom}
                  data-testid="rc-custom-save"
                >
                  {savingCustom
                    ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Saving…</>
                    : <><Sparkles className="w-3.5 h-3.5 mr-1" /> Save + generate photo</>}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
